import React, { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonFooter, IonIcon, IonInput, IonText, IonSpinner, IonLabel,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { UserManagementUserResponse } from '@/types/response/userManagementResponses';

interface ResetPasswordModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    user: UserManagementUserResponse | null;
    onSaved: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
    isOpen, onDidDismiss, user, onSaved,
}) => {
    const [password, setPassword] = useState('');
    const [rePassword, setRePassword] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const forceClosingRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            forceClosingRef.current = false;
            setPassword('');
            setRePassword('');
            setErrors({});
        }
    }, [isOpen]);

    const validate = (): boolean => {
        const e: Record<string, string> = {};
        if (!password) e.password = 'Temporary Password is required.';
        if (!rePassword) {
            e.rePassword = 'Please re-enter the password.';
        } else if (password && password !== rePassword) {
            e.rePassword = 'Passwords do not match.';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!user || !validate()) return;
        setIsSaving(true);
        try {
            await apiService.resetUserPassword({
                Id: user.Id,
                UserName: user.UserName,
                Password: password,
                RePassword: rePassword,
            });
            setToast({ message: 'Password reset successfully.', variant: 'success' });
            onSaved();
            forceClosingRef.current = true;
            onDidDismiss();
        } catch {
            setToast({ message: 'Failed to reset password.', variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const title = user ? `${user.FirstName} ${user.LastName} — Reset Password` : 'Reset Password';

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={() => { if (!forceClosingRef.current) onDidDismiss(); }}
                style={{ '--width': '440px', '--height': '340px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>{title}</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={onDidDismiss}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1.25rem', '--padding-end': '1.25rem', '--padding-top': '1rem', '--padding-bottom': '0.5rem' }}>
                    {isSaving ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><IonSpinner /></div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>User Name</IonLabel>
                                <IonInput value={user?.UserName ?? ''} readonly
                                    style={{ border: '1px solid var(--ion-color-light-shade)', borderRadius: '4px', opacity: 0.7 }} />
                            </div>
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Temporary Password *</IonLabel>
                                <IonInput type="password" value={password} onIonInput={e => { setPassword(e.detail.value ?? ''); setErrors(p => ({ ...p, password: '' })); }}
                                    style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                {errors.password && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.password}</IonText>}
                            </div>
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.25rem' }}>Re-enter Password *</IonLabel>
                                <IonInput type="password" value={rePassword} onIonInput={e => { setRePassword(e.detail.value ?? ''); setErrors(p => ({ ...p, rePassword: '' })); }}
                                    style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
                                {errors.rePassword && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors.rePassword}</IonText>}
                            </div>
                        </div>
                    )}
                </IonContent>

                {!isSaving && (
                    <IonFooter>
                        <IonToolbar style={{ '--padding-start': '1rem', '--padding-end': '1rem' }}>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={onDidDismiss}>Cancel</IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton fill="solid" color="primary" onClick={handleSave}>Reset Password</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
        </>
    );
};

export default ResetPasswordModal;
